import React from 'react';
import { BarChart, yAxisSides } from 'react-gifted-charts';
import { tokens } from 'tamagui-config';

export interface EnergyCostChartProps {
  thisWeekCosts: { value: number; label: string }[];
  lastWeekCosts: { value: number; label: string }[];
}

export const EnergyCostChart: React.FC<EnergyCostChartProps> = ({
  thisWeekCosts,
  lastWeekCosts,
}) => {
  const formatYLabel = (value: string) =>
    Number(value) === 0 ? '$0' : Number(value) < 10 ? `$${value}` : `$${value}`;

  // Get the last 7 days for both weeks and format them with day abbreviations
  const processWeekData = (weekData: { value: number; label: string }[]) =>
    weekData
      .sort((a, b) => new Date(a.label).getTime() - new Date(b.label).getTime()) // Sort chronologically
      .slice(-7) // Get the last 7 days
      .map((item) => {
        const date = new Date(item.label);
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const dayName = dayNames[date.getUTCDay()];

        return {
          value: item.value,
          label: dayName,
        };
      });

  const thisWeekData = processWeekData(thisWeekCosts);
  const lastWeekData = processWeekData(lastWeekCosts);

  // Create a dynamic 7-day structure starting from 7 days ago (ending yesterday)
  const today = new Date();
  const dayNamesFull = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const last7Days: string[] = [];

  for (let i = 7; i >= 1; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const dayName = dayNamesFull[date.getDay()];
    last7Days.push(dayName);
  }

  const chartData: Array<{
    value: number;
    spacing?: number;
    frontColor: string;
    label?: string;
  }> = [];

  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayName = last7Days[dayIndex];

    // Find data for this specific day of the week in each dataset
    const lastWeekItem = lastWeekData.find((item) => item.label === dayName);
    const thisWeekItem = thisWeekData.find((item) => item.label === dayName);

    if (lastWeekItem) {
      // Add last week bar (darker blue)
      chartData.push({
        value: lastWeekItem.value,
        spacing: 3,
        frontColor: tokens.color.hgTertiary,
      });
    } else {
      // Add placeholder bar with 0 value if no data for this day
      chartData.push({
        value: 0,
        spacing: 3,
        frontColor: 'transparent',
      });
    }

    if (thisWeekItem) {
      // Add this week bar (bright blue) with label
      chartData.push({
        value: thisWeekItem.value,
        spacing: dayIndex < 6 ? 28 : 0, // Larger space between day groups
        frontColor: tokens.color.hgPrimary,
        label: thisWeekItem.label,
      });
    } else {
      // Add placeholder bar with 0 value if no data for this day
      chartData.push({
        value: 0,
        spacing: dayIndex < 6 ? 28 : 0,
        frontColor: 'transparent',
        label: dayName, // Still show the day label
      });
    }
  }

  return (
    <BarChart
      data={chartData}
      height={200}
      barWidth={15}
      initialSpacing={20}
      spacing={35}
      endSpacing={20}
      noOfSections={2}
      roundedTop
      rulesColor={tokens.color.white6}
      rulesType="solid"
      xAxisThickness={0}
      xAxisLabelTextStyle={{
        color: tokens.color.white12,
        fontSize: 24,
      }}
      yAxisThickness={0}
      yAxisLabelWidth={60}
      yAxisSide={yAxisSides.RIGHT}
      formatYLabel={formatYLabel}
      yAxisTextStyle={{
        color: tokens.color.white12,
        fontSize: 24,
      }}
      yAxisLabelContainerStyle={{
        paddingTop: 5,
        marginLeft: 10,
        paddingRight: 15,
      }}
      showReferenceLine1
      referenceLinesOverChartContent={false}
    />
  );
};
